// Matriz tipo × gama: qué reglas activas caen en cada combinación del catálogo. Como el mock, solo
// mira tipos y gamas del alcance; SKUs, tiendas, condición y vigencia no entran.
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionRuleStatusEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionRuleStatusEnum';
import { ProductCategoryEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductCategoryEnum';
import { ProductTierEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductTierEnum';
import { CATEGORY_LABEL, describeLevers, TIER_LABEL } from './ruleText';

type Rule = RetailCommission.CommissionRule;

export interface MatrixColumn {
    key: string;
    label: string;
    category: ProductCategoryEnum;
    tier: ProductTierEnum | null;
}

const { PHONE, SIM, MODEM, ACCESSORY, BUNDLE } = ProductCategoryEnum;
const PHONE_TIERS = [
    ProductTierEnum.PREMIUM,
    ProductTierEnum.HIGH,
    ProductTierEnum.MID,
    ProductTierEnum.LOW,
];

/** La gama solo se cruza con el teléfono, como en el mock. */
export const MATRIX_COLUMNS: readonly MatrixColumn[] = [
    ...PHONE_TIERS.map(
        (tier): MatrixColumn => ({
            key: `PHONE-${tier}`,
            label: `${CATEGORY_LABEL.PHONE} ${TIER_LABEL[tier]}`,
            category: PHONE,
            tier,
        }),
    ),
    ...[SIM, MODEM, ACCESSORY, BUNDLE].map(
        (category): MatrixColumn => ({
            key: category,
            label: CATEGORY_LABEL[category],
            category,
            tier: null,
        }),
    ),
];

export type MatrixGroup = 'Bono/venta' | 'Multiplicador' | 'Bono umbral' | 'Renta de cartera';

const MATRIX_GROUPS: readonly MatrixGroup[] = [
    'Bono/venta',
    'Multiplicador',
    'Bono umbral',
    'Renta de cartera',
];

/** El grupo sale de la primera palanca, en el orden venta → multiplicador → umbral → cupón. */
export function matrixGroupOf(levers: RetailCommission.CommissionRuleLevers): MatrixGroup | null {
    if (levers.bonoPorVenta) return 'Bono/venta';
    if (levers.multiplicador) return 'Multiplicador';
    if (levers.bonoUmbral) return 'Bono umbral';
    if (levers.bonoPorCuponPagado) return 'Renta de cartera';
    return null;
}

function appliesTo(scope: RetailCommission.CommissionRuleScope, column: MatrixColumn): boolean {
    const typeMatches = scope.tipos.length === 0 || scope.tipos.includes(column.category);
    const tierMatches =
        scope.gamas.length === 0 || (column.tier !== null && scope.gamas.includes(column.tier));
    return typeMatches && tierMatches;
}

export interface MatrixRow {
    rule: Rule;
    cells: Array<string | null>;
}

export interface Matrix {
    groups: Array<{ group: MatrixGroup; rows: MatrixRow[] }>;
    /** Cuántas reglas caen en cada columna. */
    counts: number[];
}

export function buildMatrix(rules: readonly Rule[]): Matrix {
    const counts = MATRIX_COLUMNS.map(() => 0);
    const groups = MATRIX_GROUPS.map((group) => ({ group, rows: [] as MatrixRow[] }));
    for (const rule of rules) {
        if (rule.status !== CommissionRuleStatusEnum.ACTIVE) continue;
        const group = matrixGroupOf(rule.levers);
        if (!group) continue;
        const value = describeLevers(rule.levers, rule.countBy);
        const cells = MATRIX_COLUMNS.map((column, index) => {
            if (!appliesTo(rule.scope, column)) return null;
            counts[index] += 1;
            return value;
        });
        groups.find((g) => g.group === group)?.rows.push({ rule, cells });
    }
    return { groups: groups.filter((g) => g.rows.length > 0), counts };
}

/** Una combinación con más de tres reglas es difícil de explicar al vendedor. */
export function countTone(count: number): 'success' | 'warning' | 'danger' {
    if (count <= 1) return 'success';
    return count <= 3 ? 'warning' : 'danger';
}
